import { Link } from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, GraduationCap } from 'lucide-react';
import AdminLayout from './AdminLayout';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Toaster, toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminCategories = () => {
  const { token } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', is_school: false, school_name: '', allows_thread_color: false });

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchCategories(); }, []);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/categories`);
      setCategories(res.data);
    } catch { toast.error('Failed to load categories'); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await axios.put(`${API_URL}/api/categories/${editing}`, form, { headers });
        toast.success('Category updated');
      } else {
        await axios.post(`${API_URL}/api/categories`, form, { headers });
        toast.success('Category created');
      }
      resetForm();
      fetchCategories();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save category'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this category?')) return;
    try {
      await axios.delete(`${API_URL}/api/categories/${id}`, { headers });
      toast.success('Category deleted');
      fetchCategories();
    } catch { toast.error('Failed to delete category'); }
  };

  const startEdit = (cat) => {
    setEditing(cat.id);
    setForm({ name: cat.name, description: cat.description || '', is_school: cat.is_school, school_name: cat.school_name || '', allows_thread_color: !!cat.allows_thread_color });
    setShowForm(true);
  };

  const resetForm = () => { setEditing(null); setShowForm(false); setForm({ name: '', description: '', is_school: false, school_name: '', allows_thread_color: false }); };

  const inputCls = "w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4A90A4] text-gray-900 text-sm";

  return (
    <AdminLayout>
      <Toaster position="top-right" richColors />
      <div className="flex items-center justify-between mb-8" data-testid="admin-categories-page">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Categories</h1>
          <p className="text-sm text-gray-500 mt-1">Manage product categories and school affiliations</p>
        </div>
        <button onClick={() => { resetForm(); setShowForm(true); }}
          className="inline-flex items-center px-5 py-2.5 bg-[#F5A623] text-gray-900 font-bold text-sm rounded-full btn-glow"
          data-testid="add-category-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Add Category
        </button>
      </div>

      {showForm && (
        <div className="mb-8 p-6 bg-white rounded-2xl border border-gray-100 shadow-sm">
          <h2 className="text-lg font-bold mb-4 text-gray-900">{editing ? 'Edit Category' : 'New Category'}</h2>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="category-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">Name *</label>
                <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className={inputCls} data-testid="category-name" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">Description</label>
                <input type="text" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls} data-testid="category-description" />
              </div>
            </div>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.is_school} onChange={(e) => setForm({ ...form, is_school: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-300 text-[#4A90A4] focus:ring-[#4A90A4]" />
                <span className="text-sm text-gray-700">This is a school category</span>
              </label>
              {form.is_school && (
                <div className="flex-1">
                  <input type="text" value={form.school_name} onChange={(e) => setForm({ ...form, school_name: e.target.value })}
                    placeholder="School name" className={inputCls} data-testid="school-name" />
                </div>
              )}
            </div>
            {form.is_school && (
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.allows_thread_color}
                  onChange={(e) => setForm({ ...form, allows_thread_color: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-300 text-[#4A90A4] focus:ring-[#4A90A4]"
                  data-testid="allows-thread-color" />
                <span className="text-sm text-gray-700">Allow customers to choose embroidery thread color for this school&apos;s products</span>
              </label>
            )}
            <div className="flex gap-3">
              <button type="submit" className="px-6 py-2.5 bg-[#4A90A4] text-white font-semibold text-sm rounded-full hover:bg-[#3d7a8e] transition-colors" data-testid="save-category-btn">
                {editing ? 'Update' : 'Create'}
              </button>
              <button type="button" onClick={resetForm} className="px-6 py-2.5 border border-gray-200 text-gray-600 text-sm rounded-full hover:bg-gray-50 transition-colors">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="h-16 bg-gray-100 rounded-xl animate-pulse" />)}</div>
      ) : categories.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
          <p className="text-gray-400 mb-4">No categories yet</p>
          <button onClick={() => setShowForm(true)} className="text-[#4A90A4] font-semibold text-sm hover:underline">Create your first category</button>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Name</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Description</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Type</th>
                <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {categories.map((cat) => (
                <tr key={cat.id} className="hover:bg-gray-50 transition-colors" data-testid={`category-row-${cat.id}`}>
                  <td className="px-6 py-4 font-medium text-gray-900">{cat.name}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{cat.description || '—'}</td>
                  <td className="px-6 py-4">
                    {cat.is_school ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#4A90A4]/10 text-[#4A90A4] text-xs font-semibold rounded-full">
                        <GraduationCap className="w-3 h-3" /> School
                      </span>
                    ) : (
                      <span className="px-2.5 py-1 bg-gray-100 text-gray-500 text-xs font-semibold rounded-full">General</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2"><Link className="whitespace-nowrap underline text-sm px-2 py-3" to={`/shop?category=${cat.slug}`} target="_blank">View shop</Link><button className="text-sm px-2 py-3 whitespace-nowrap" onClick={()=>navigator.clipboard.writeText(`${window.location.origin}/shop?category=${cat.slug}`).then(()=>toast.success('Shop link copied')).catch(()=>toast.error('Open View shop and copy its address.'))}>Copy link</button>
                      <button onClick={() => startEdit(cat)} className="p-2 text-gray-400 hover:text-[#4A90A4] transition-colors rounded-lg hover:bg-gray-100">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDelete(cat.id)} className="p-2 text-gray-400 hover:text-red-500 transition-colors rounded-lg hover:bg-gray-100">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminCategories;
